import { NavigateFunction } from 'react-router-dom'
import { Path } from 'constants/router'
import { logger } from 'utils/logging'

/**
 * Pages reached from outside the app (plugin link, bookmark, a `replace: true` redirect) have no in-app entry to go
 * back to, so a plain `navigate(-1)` is a silent no-op. Seven other `navigate(-1)` call sites in `src/` carry the same
 * latent bug on any deep-linkable page and are one-line candidates for this helper.
 *
 * `window.history.state.idx` is React Router's own stack position, not a web-platform API. Verified against the 6.30
 * in use: `createBrowserHistory` seeds it at 0 and maintains it across push and replace, and `replace: true` preserves
 * it — which is what makes an out-of-app entry resolve to the dashboard. A router upgrade that renames or drops it
 * degrades this to "always home" rather than breaking, hence the warn instead of a throw.
 */
export const navigateBackOrHome = (navigate: NavigateFunction) => {
  const idx = window.history.state?.idx

  // A fresh tab legitimately has no state yet. A state object *without* a numeric idx means either a third-party script
  // clobbered it (Intercom, Fullstory, Hotjar, Userpilot and GA all load here) or React Router moved the field
  if (!!window.history.state && typeof idx !== 'number') {
    logger.warn('history.state carries no React Router index, falling back to the dashboard', undefined, {
      context: {
        component: 'navigateBackOrHome',
        action: 'resolveExit',
        metadata: { historyStateKeys: Object.keys(window.history.state) },
      },
    })
  }

  if ((idx ?? 0) > 0) navigate(-1)
  else navigate(Path.INDEX)
}
